import React from 'react';
import { FaPython, FaReact, FaAws, FaDocker, FaEthereum, FaNodeJs } from "react-icons/fa";
import {
  SiGooglecloud, SiKubernetes, SiPostgresql, SiMongodb,
  SiTypescript, SiSolidity, SiApachekafka, SiNextdotjs,
  SiRedis, SiRust, SiHuggingface, SiFlutter
} from "react-icons/si";
import { TbBrandReactNative } from "react-icons/tb";
import { usePersona } from '../persona/PersonaContext';

const ICONS = {
  python:      { node: <FaPython />,      label: 'Python' },
  typescript:  { node: <SiTypescript />,  label: 'TypeScript' },
  aws:         { node: <FaAws />,         label: 'AWS' },
  gcp:         { node: <SiGooglecloud />, label: 'GCP' },
  docker:      { node: <FaDocker />,      label: 'Docker' },
  kubernetes:  { node: <SiKubernetes />,  label: 'Kubernetes' },
  kafka:       { node: <SiApachekafka />, label: 'Kafka' },
  postgres:    { node: <SiPostgresql />,  label: 'PostgreSQL' },
  mongodb:     { node: <SiMongodb />,     label: 'MongoDB' },
  react:       { node: <FaReact />,       label: 'React' },
  solidity:    { node: <SiSolidity />,    label: 'Solidity' },
  ethereum:    { node: <FaEthereum />,    label: 'Ethereum' },
  nextjs:      { node: <SiNextdotjs />,   label: 'Next.js' },
  nodejs:      { node: <FaNodeJs />,      label: 'Node.js' },
  rust:        { node: <SiRust />,        label: 'Rust' },
  redis:       { node: <SiRedis />,       label: 'Redis' },
  huggingface: { node: <SiHuggingface />, label: 'Hugging Face' },
  flutter:     { node: <SiFlutter />,     label: 'Flutter' },
  reactnative: { node: <TbBrandReactNative />, label: 'React Native' },
};

const BioSkillsSection = () => {
  const persona = usePersona();
  const { bio } = persona;

  const techIcons = (bio.techIcons || [])
    .map((key) => ICONS[key])
    .filter(Boolean);

  return (
    <section className="bg-matrix-bg py-24 relative overflow-hidden">
      {/* Background scanlines */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-full h-px bg-matrix-phosphor"
            style={{ top: `${i * 5}%` }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="font-mono text-xs text-matrix-dim tracking-[0.3em] mb-10">
          [ 003 · BIO.SKILLS ]
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          {/* Left Column - Bio */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="font-kelly text-3xl text-matrix-phosphor"
                  style={{ textShadow: '0 0 10px rgba(0,255,102,0.45)' }}>
                &gt; {bio.heading}
              </h2>
              <div className="flex flex-wrap gap-2 font-mono text-[11px]">
                <div className="bg-matrix-surface px-3 py-2 border border-matrix-dim/40">
                  <span className="text-matrix-pale uppercase tracking-wider">cert::aws-cloud-practitioner</span>
                </div>
                <div className="bg-matrix-surface px-3 py-2 border border-matrix-dim/40">
                  <span className="text-matrix-pale uppercase tracking-wider">cert::gcp-associate-engineer</span>
                </div>
                <div className="bg-matrix-surface px-3 py-2 border border-matrix-dim/40">
                  <span className="text-matrix-pale uppercase tracking-wider">cert::gcp-digital-leader</span>
                </div>
              </div>
            </div>

           <p className="font-mono text-sm text-matrix-pale leading-relaxed">
              <span className="text-matrix-dim">{'//'}</span> {bio.paragraph}
            </p>

            <div className="relative border-l-2 border-matrix-phosphor pl-6 space-y-4">
              <p className="font-mono text-sm text-matrix-phosphor uppercase tracking-[0.15em]">
                &gt; currently_digging_into:
              </p>
              <ul className="space-y-2 text-matrix-pale font-mono text-sm">
                {bio.diggingInto.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-matrix-phosphor mt-1">▸</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
               </div>

          {/* Right Column - Skills */}
          <div className="space-y-12">
            {/* Expertise Grid */}
            <div className="space-y-4">
              <h3 className="font-mono text-matrix-phosphor text-sm uppercase tracking-[0.25em]">
                &gt; expertise[]
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {bio.expertise.map((skill, index) => (
                  <div
                    key={index}
                    className="bg-matrix-surface p-3 border border-matrix-dim/40 hover:border-matrix-phosphor hover:shadow-phosphor-sm transition-all"
                  >
                    <span className="font-mono text-matrix-pale text-xs uppercase tracking-wider">{skill}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Technologies */}
            <div className="space-y-4">
              <h3 className="font-mono text-matrix-phosphor text-sm uppercase tracking-[0.25em]">
                &gt; stack.loaded()
              </h3>
              <div className="flex flex-wrap gap-2">
                {techIcons.map((tech, index) => (
                  <div
                    key={index}
                    className="group flex items-center gap-2 bg-matrix-surface px-3 py-2 border border-matrix-dim/40 hover:border-matrix-phosphor hover:shadow-phosphor-sm transition-all"
                  >
                    <span className="text-matrix-dim group-hover:text-matrix-phosphor transition-colors">
                      {tech.node}
                    </span>
                    <span className="font-mono text-matrix-pale group-hover:text-matrix-phosphor transition-colors text-xs uppercase tracking-wider">
                      {tech.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Fun Fact */}
            <div className="relative bg-matrix-surface p-6 border border-matrix-dim/40">
              <div className="absolute -top-2 left-4 bg-matrix-bg px-2 font-mono text-[10px] text-matrix-phosphor uppercase tracking-[0.25em]">
                {'/* design_principle */'}
              </div>
              <p className="font-mono text-sm text-matrix-pale italic leading-relaxed">
                "If it breaks, my users don't have a helpdesk to call. So it doesn't break."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BioSkillsSection;
